
import { useState } from "react";
import { useSubmittedDocuments } from "../../../hooks/admin/identity-docs/useSubmittedDocuments";
import { SubmittedDocsTable } from "./SubmittedDocsTable";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePickerWithRange } from "@/components/ui/date-picker-with-range";
import { DateRange } from "react-day-picker";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserGroupedDocuments } from "./UserGroupedDocuments";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import PollsPagination from "../polls/PollsPagination";
import { usePagination } from "@/hooks/polls/usePagination";

export function SubmittedDocuments() {
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState<"list" | "grouped">("list");
  const [filter, setFilter] = useState<{
    search: string;
    status: string;
    country: string;
    dateRange: DateRange | undefined;
    groupByUser: boolean;
  }>({
    search: "",
    status: "all",
    country: "all",
    dateRange: undefined,
    groupByUser: false
  });
  
  const { documents, userGroups, isLoading, error, refetch } = useSubmittedDocuments(filter);

  const handleGroupChange = (checked: boolean) => {
    setFilter(prev => ({ ...prev, groupByUser: checked }));
    setViewMode(checked ? "grouped" : "list");
  };
  
  // Add pagination
  const pagination = usePagination({
    totalItems: viewMode === "list" ? documents.length : userGroups.length,
    initialPage: 1,
    itemsPerPage: 10
  });
  
  // Get paginated items based on view mode
  const paginatedDocuments = pagination.paginatedItems(documents);
  const paginatedUserGroups = pagination.paginatedItems(userGroups);

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h2 className="text-lg md:text-xl lg:text-2xl font-bold tracking-tight mb-4">Submitted Documents</h2>
        
        {/* Mobile-first filter layout */}
        <div className="space-y-3 mb-4">
          {/* Search input - full width on mobile */}
          <Input
            placeholder="Search by name or email..."
            value={filter.search}
            onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
            className="w-full"
          />
          
          {/* Status and Country filters */}
          <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-3'}`}>
            <Select
              value={filter.status}
              onValueChange={(value) => setFilter(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={filter.country}
              onValueChange={(value) => setFilter(prev => ({ ...prev, country: value }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {/* Add country options dynamically */}
              </SelectContent>
            </Select>
            
            {/* Date picker - full width on mobile */}
            <div className={isMobile ? 'col-span-1' : ''}>
              <DatePickerWithRange
                selected={filter.dateRange}
                onSelect={(range) => setFilter(prev => ({ ...prev, dateRange: range }))}
                className="w-full"
              />
            </div>
          </div>
        </div>
        
        {/* Group by user toggle */}
        <div className="flex items-center space-x-2 mb-4">
          <Switch 
            id="group-by-user" 
            checked={filter.groupByUser}
            onCheckedChange={handleGroupChange}
          />
          <Label htmlFor="group-by-user" className="text-sm">Group by user</Label>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-nation-blue"></div>
        </div>
      ) : error ? (
        <div className="p-4 text-destructive">Error loading documents: {(error as Error).message}</div>
      ) : (
        <>
          {viewMode === "list" ? (
            <SubmittedDocsTable documents={paginatedDocuments} onDocumentUpdated={refetch} />
          ) : (
            <UserGroupedDocuments userGroups={paginatedUserGroups} onDocumentUpdated={refetch} />
          )}
          
          {/* Pagination */}
          {(viewMode === "list" ? documents.length : userGroups.length) > 0 && (
            <div className="overflow-x-auto">
              <PollsPagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                itemsPerPage={pagination.itemsPerPage}
                totalItems={viewMode === "list" ? documents.length : userGroups.length}
                startIndex={pagination.startIndex}
                endIndex={Math.min(pagination.endIndex, viewMode === "list" ? documents.length : userGroups.length)}
                onPageChange={pagination.setCurrentPage}
                onItemsPerPageChange={pagination.setItemsPerPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
